/**
 * Relatórios > Financeiro > Recebíveis de Cartão.
 *
 * O detalhe por trás da previsão que entra no fluxo de caixa projetado, em três
 * níveis:
 *
 *   dia (o que cai na conta da empresa) → bandeira → parcela do pedido
 *
 * O nível do meio não é enfeite: o depósito da adquirente chega no extrato
 * agregado por bandeira/dia, e é assim que a planilha de recebíveis da Cielo
 * agrega. É nesse nível que a conciliação vai casar nas duas pontas.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from '@components/Button';
import { DateInputBR } from '@components/DateInputBR';
import { Card, CardContent } from '@components/Card';
import { financialReportsService } from '@services/financialReportsService';
import { formatCurrencyBRL, formatDateBR } from '@pages/financial/financialUtils';
import { inputCls } from '@pages/financial/financialShared';
import type {
  CardReceivableDayGroup,
  CardReceivableSchedule,
} from '@/types/cardReceivable';

/** Estes valores já chegam em reais; a exibição é sempre pt-BR. */
const formatCurrency = (value: number): string => formatCurrencyBRL(value);

/** Datas: sempre dd/MM/aaaa, via o helper compartilhado do módulo. */
const formatDate = (iso: string): string => formatDateBR(iso);

const WEEKDAYS = ['domingo', 'segunda', 'terça', 'quarta', 'quinta', 'sexta', 'sábado'];

/** O dia da semana confirma, a olho, que a data caiu em dia útil. */
const weekdayOf = (iso: string): string => {
  const [y, m, d] = iso.split('-').map(Number);
  return WEEKDAYS[new Date(y, m - 1, d).getDay()];
};

const today = (): string => new Date().toISOString().slice(0, 10);
const inDays = (days: number): string => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

const dayKey = (day: CardReceivableDayGroup): string => `${day.expected_date}:${day.company_id}`;

export const CardReceivablesView: React.FC = () => {
  const [schedule, setSchedule] = useState<CardReceivableSchedule | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [dateFrom, setDateFrom] = useState(today());
  const [dateTo, setDateTo] = useState(inDays(60));
  const [onlyPending, setOnlyPending] = useState(false);

  const [expandedDays, setExpandedDays] = useState<Set<string>>(new Set());
  const [expandedBrands, setExpandedBrands] = useState<Set<string>>(new Set());

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await financialReportsService.getCardReceivables({
        dateFrom,
        dateTo,
        onlyPending,
      });
      setSchedule(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar os recebíveis');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggle = (set: Set<string>, key: string): Set<string> => {
    const next = new Set(set);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    return next;
  };

  const days = schedule?.days ?? [];

  /** Total por empresa, para o cabeçalho: é o que cada CNPJ tem a receber. */
  const byCompany = useMemo(() => {
    const map = new Map<string, number>();
    for (const day of days) {
      map.set(day.company_name, (map.get(day.company_name) ?? 0) + day.net_amount);
    }
    return Array.from(map.entries()).sort((a, b) => b[1] - a[1]);
  }, [days]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-slate-600">
          O que a adquirente vai creditar, por dia de pagamento. Abra um dia para ver as bandeiras
          — que é como o depósito aparece no extrato — e a bandeira para ver as parcelas de cada
          pedido, com o bruto capturado, a taxa aplicada e o líquido. Para <strong>importar a
          planilha da Cielo</strong> ou <strong>conciliar com o extrato</strong>, vá em Financeiro →
          Conta Corrente: aquilo gera lançamento e precisa da conta à vista.
        </p>
        <Button variant="outline" size="sm" onClick={load} icon={<RefreshCw size={14} />}>
          Atualizar
        </Button>
      </div>

      {error && (
        <Card className="bg-red-50 border-red-200">
          <CardContent><p className="text-red-600 text-sm">{error}</p></CardContent>
        </Card>
      )}

      <Card>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 items-end">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">De</label>
              <DateInputBR className={inputCls} value={dateFrom} onChange={setDateFrom} />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Até</label>
              <DateInputBR className={inputCls} value={dateTo} onChange={setDateTo} />
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-700 pb-2">
              <input
                type="checkbox"
                checked={onlyPending}
                onChange={(e) => setOnlyPending(e.target.checked)}
                className="rounded border-slate-300"
              />
              Somente o que ainda não foi liquidado
            </label>
            <Button size="sm" onClick={load} loading={loading}>Aplicar</Button>
          </div>
        </CardContent>
      </Card>

      {schedule && schedule.without_fee_count > 0 && (
        <Card className="bg-amber-50 border-amber-200">
          <CardContent>
            <p className="text-sm text-amber-800 flex items-start gap-2">
              <AlertTriangle size={16} className="mt-0.5 shrink-0" />
              <span>
                <strong>{schedule.without_fee_count}</strong> parcela(s) sem taxa cadastrada para a
                bandeira ou a faixa de parcelas. O líquido previsto delas está igual ao bruto —
                cadastre a taxa em Hub de Recebimentos → Taxas de Cartão e reprocesse.
              </span>
            </p>
          </CardContent>
        </Card>
      )}

      {schedule && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Card>
            <CardContent>
              <p className="text-xs text-slate-500 uppercase tracking-wider">Bruto no período</p>
              <p className="text-lg font-bold text-slate-800 mt-1">{formatCurrency(schedule.gross_amount)}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent>
              <p className="text-xs text-slate-500 uppercase tracking-wider">Taxas</p>
              <p className="text-lg font-bold text-red-600 mt-1">-{formatCurrency(schedule.fee_amount)}</p>
            </CardContent>
          </Card>
          <Card className="bg-emerald-50 border-emerald-200">
            <CardContent>
              <p className="text-xs text-emerald-600 uppercase tracking-wider font-medium">A receber (líquido)</p>
              <p className="text-lg font-bold text-emerald-700 mt-1">{formatCurrency(schedule.net_amount)}</p>
              {byCompany.length > 1 && (
                <p className="text-xs text-emerald-700/80 mt-1">
                  {byCompany.map(([name, total]) => `${name}: ${formatCurrency(total)}`).join(' · ')}
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {loading && !schedule ? (
        <Card><CardContent><div className="h-24 bg-slate-100 rounded animate-pulse" /></CardContent></Card>
      ) : days.length === 0 ? (
        <Card>
          <CardContent>
            <p className="text-center text-slate-500 py-8">
              Nenhum recebível previsto no período. A agenda é gerada a partir das transações
              capturadas — se nada foi capturado, não há o que receber.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {days.map((day) => {
            const dKey = dayKey(day);
            const dayOpen = expandedDays.has(dKey);
            return (
              <Card key={dKey}>
                <CardContent className="p-0">
                  {/* Nível 1 — o que cai na conta da empresa nesse dia */}
                  <button
                    onClick={() => setExpandedDays(toggle(expandedDays, dKey))}
                    className="w-full flex items-center justify-between gap-3 px-4 py-3 hover:bg-slate-50 transition-colors text-left"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      {dayOpen ? <ChevronDown size={16} className="text-slate-400 shrink-0" /> : <ChevronRight size={16} className="text-slate-400 shrink-0" />}
                      <div className="min-w-0">
                        <p className="font-medium text-slate-800">
                          {formatDate(day.expected_date)}
                          <span className="text-slate-400 font-normal"> · {weekdayOf(day.expected_date)}</span>
                        </p>
                        <p className="text-xs text-slate-500 truncate">
                          {day.company_name} · {day.count} parcela(s)
                          {day.settled_count > 0 && ` · ${day.settled_count} liquidada(s)`}
                        </p>
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="font-semibold text-emerald-700">{formatCurrency(day.net_amount)}</p>
                      <p className="text-xs text-slate-500">
                        bruto {formatCurrency(day.gross_amount)} · taxa {formatCurrency(day.fee_amount)}
                      </p>
                    </div>
                  </button>

                  {dayOpen && (
                    <div className="border-t border-slate-100">
                      {day.brands.map((brand) => {
                        const bKey = `${dKey}:${brand.brand}`;
                        const brandOpen = expandedBrands.has(bKey);
                        return (
                          <div key={bKey} className="border-b border-slate-50 last:border-b-0">
                            {/* Nível 2 — bandeira: a granularidade do depósito */}
                            <button
                              onClick={() => setExpandedBrands(toggle(expandedBrands, bKey))}
                              className="w-full flex items-center justify-between gap-3 pl-10 pr-4 py-2.5 hover:bg-slate-50 transition-colors text-left"
                            >
                              <div className="flex items-center gap-2">
                                {brandOpen ? <ChevronDown size={14} className="text-slate-400" /> : <ChevronRight size={14} className="text-slate-400" />}
                                <span className="font-medium text-slate-700">{brand.brand}</span>
                                <span className="text-xs text-slate-500">{brand.count} parcela(s)</span>
                              </div>
                              <span className="font-medium text-slate-700">{formatCurrency(brand.net_amount)}</span>
                            </button>

                            {brandOpen && (
                              <div className="bg-slate-50/60 px-4 pb-3">
                                <table className="w-full text-sm">
                                  <thead>
                                    <tr className="text-xs text-slate-500 uppercase tracking-wider">
                                      <th className="text-left font-medium py-2 pl-6">Pedido</th>
                                      <th className="text-left font-medium py-2">Parcela</th>
                                      <th className="text-right font-medium py-2">Bruto</th>
                                      <th className="text-right font-medium py-2">Taxa</th>
                                      <th className="text-right font-medium py-2">Líquido</th>
                                      <th className="text-left font-medium py-2 pl-4">Situação</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {/* Nível 3 — a parcela de cada pedido */}
                                    {brand.items.map((item) => (
                                      <tr key={item.id} className="border-t border-slate-200/70">
                                        <td className="py-2 pl-6">
                                          <span className="text-slate-800">{item.reference}</span>
                                          <span className="block text-xs text-slate-400">
                                            capturado em {formatDate(item.captured_on)}
                                          </span>
                                        </td>
                                        <td className="py-2 text-slate-600">
                                          {item.installment_number}/{item.installments}
                                        </td>
                                        <td className="py-2 text-right text-slate-700">{formatCurrency(item.gross_amount)}</td>
                                        <td className="py-2 text-right text-red-600">
                                          {item.has_fee ? (
                                            <>
                                              -{formatCurrency(item.fee_amount)}
                                              <span className="block text-xs text-slate-400">
                                                {item.fee_percent.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}%
                                              </span>
                                            </>
                                          ) : (
                                            <span className="text-amber-600 text-xs inline-flex items-center gap-1">
                                              <AlertTriangle size={12} /> sem taxa cadastrada
                                            </span>
                                          )}
                                        </td>
                                        <td className="py-2 text-right font-medium text-emerald-700">
                                          {formatCurrency(item.net_amount)}
                                        </td>
                                        <td className="py-2 pl-4">
                                          <span
                                            className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                              item.status === 1
                                                ? 'bg-emerald-100 text-emerald-700'
                                                : item.status === 2
                                                  ? 'bg-red-100 text-red-700'
                                                  : 'bg-slate-100 text-slate-600'
                                            }`}
                                          >
                                            {item.status_label}
                                          </span>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};
